/* eslint-disable react/jsx-no-bind */
import {
  ArrowBack,
  ArrowForward,
  Search,
  Close,
  Refresh,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';

import kycService from './kycService';

const ManualKYC = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [kycIdFilter, setKycIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [kycTypeFilter, setKycTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycDocuments, setKycDocuments] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionCategory, setRejectionCategory] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [aadhaarError, setAadhaarError] = useState('');
  const [aadhaarVerified, setAadhaarVerified] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const getFormattedDateRange = (opt = {}) => {
    const fromSource =
      opt.fromDate !== undefined ? opt.fromDate : fromDate;
    const toSource = opt.toDate !== undefined ? opt.toDate : toDate;
    const start =
      fromSource != null && dayjs(fromSource).isValid()
        ? dayjs(fromSource).format('YYYY-MM-DD')
        : null;
    const end =
      toSource != null && dayjs(toSource).isValid()
        ? dayjs(toSource).format('YYYY-MM-DD')
        : null;
    return { start, end };
  };

  const fetchPendingKYC = async (opt = {}) => {
    setLoading(true);
    const apiPage = (opt.page ?? page) + 1;
    const apiLimit = opt.pageSize ?? rowsPerPage;
    const kycId = opt.kycId !== undefined ? opt.kycId : kycIdFilter;
    const name = opt.name !== undefined ? opt.name : nameFilter;
    const kycType = opt.kycType !== undefined ? opt.kycType : kycTypeFilter;
    const status = opt.status !== undefined ? opt.status : statusFilter;
    const { start, end } = getFormattedDateRange(opt);

    try {
      const res = await kycService.fetchKYCRecords({
        page: apiPage,
        limit: apiLimit,
        kycId: kycId || '',
        name: name || '',
        kycType: kycType || '',
        fromDate: start,
        toDate: end,
        status: status || '',
      });
      const kycRecords = res?.data?.kyc_records || [];
      const total = res?.data?.total || 0;
      setData(kycRecords);
      setTotalCount(total);
    } catch (err) {
      logger.error('KYC fetch error:', err);
      toast.error('Error fetching KYC records');
      setData([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPendingKYC({ page: 0, pageSize: 5 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPendingKYC({ page: newPage, pageSize: rowsPerPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPendingKYC({ page: 0, pageSize: newRowsPerPage });
  };

  const handleSearch = () => {
    setPage(0);
    fetchPendingKYC({ page: 0, pageSize: rowsPerPage });
  };

  const handleReset = () => {
    setKycIdFilter('');
    setNameFilter('');
    setKycTypeFilter('');
    setStatusFilter('');
    setFromDate(null);
    setToDate(null);
    setPage(0);
    setRowsPerPage(5);
    fetchPendingKYC({
      page: 0,
      pageSize: 5,
      kycId: '',
      name: '',
      kycType: '',
      status: '',
      fromDate: null,
      toDate: null,
    });
  };

  const handleManualKyc = (kycRecord) => {
    setSelectedCustomer(kycRecord);

    const kycData = {
      documentName: kycRecord.json?.name || 'N/A',
      documentDob: kycRecord.json?.dob || 'N/A',
      documentId: kycRecord.aadhaarNumber || 'N/A',
      documentImages: [
        {
          url: kycRecord.aadharFrontUrl,
          label: 'Aadhaar Card - Front Side',
        },
        {
          url: kycRecord.aadharBackUrl,
          label: 'Aadhaar Card - Back Side',
        },
        {
          url: kycRecord.userImageUrl,
          label: 'Selfie Verification Photo',
        },
      ].filter((img) => img.url && img.url !== null && img.url !== undefined), // Filter out null/undefined URLs
    };

    setKycDocuments(kycData);
    setAadhaarNumber(kycRecord.aadhaarNumber || '');
    setKycModalOpen(true);
  };

  const handleKycApproval = async () => {
    if (window.confirm('Are you sure you want to approve this KYC?')) {
      try {
        setLoading(true);
        await kycService.approveRejectKYC(selectedCustomer.kycId, true);
        toast.success('KYC approved successfully!');
        setKycModalOpen(false);
        fetchPendingKYC({ page, pageSize: rowsPerPage });
      } catch (error) {
        toast.error('Failed to approve KYC');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKycRejection = async () => {
    if (!rejectionCategory) {
      toast.error('Please select a rejection category');
      return;
    }
    if (window.confirm('Are you sure you want to reject this KYC?')) {
      try {
        setLoading(true);
        const finalReason = `${rejectionCategory}: ${rejectionReason}`;
        await kycService.approveRejectKYC(
          selectedCustomer.kycId,
          false,
          finalReason,
        );
        toast.success('KYC rejected successfully');
        setKycModalOpen(false);
        setRejectionReason('');
        setRejectionCategory('');
        fetchPendingKYC({ page, pageSize: rowsPerPage });
      } catch (error) {
        toast.error('Failed to reject KYC');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetImageControls = () => {
    setAadhaarNumber('');
    setAadhaarError('');
    setAadhaarVerified(false);
    setCurrentImageIndex(0);
    setRejectionReason('');
    setRejectionCategory('');
  };

  const validateAadhaar = (aadhaar) => {
    const aadhaarRegex = /^[2-9]{1}[0-9]{3}\s[0-9]{4}\s[0-9]{4}$/;
    return aadhaarRegex.test(aadhaar);
  };

  const handleAadhaarVerify = () => {
    if (!aadhaarNumber.trim()) {
      setAadhaarError('Aadhaar number is required');
      return;
    }
    if (!validateAadhaar(aadhaarNumber)) {
      setAadhaarError('Please enter valid Aadhaar number (XXXX XXXX XXXX)');
      return;
    }
    setAadhaarError('');
    setAadhaarVerified(true);
    toast.success('Aadhaar number verified successfully!');
  };

  const formatAadhaar = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,4})(\d{0,4})(\d{0,4})$/);
    if (match) {
      return [match[1], match[2], match[3]].filter(Boolean).join(' ');
    }
    return value;
  };

  const nextImage = () => {
    if (kycDocuments?.documentImages) {
      setCurrentImageIndex((prev) =>
        prev < kycDocuments.documentImages.length - 1 ? prev + 1 : 0,
      );
    }
  };

  const prevImage = () => {
    if (kycDocuments?.documentImages) {
      setCurrentImageIndex((prev) =>
        prev > 0 ? prev - 1 : kycDocuments.documentImages.length - 1,
      );
    }
  };

  // Render KYC status as a styled Chip component (matches API: "approved", "in review", "kyc not done", "reject"/"rejected")
  const renderKycStatusChip = (status, size = 'small') => {
    const s = (status || '').toString().toLowerCase().trim();

    if (s === 'approved') {
      return (
        <Chip
          label="Approved"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.greenAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }

    if (s === 'reject' || s === 'rejected') {
      return (
        <Chip
          label="Rejected"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.redAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }

    if (s === 'in review') {
      return (
        <Chip
          label="In Review"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.blueAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }

    // "kyc not done" or any other / empty
    return (
      <Chip
        label="KYC Not Done"
        size={size}
        sx={{
          height: size === 'small' ? 20 : 24,
          fontSize: size === 'small' ? '11px' : '12px',
          bgcolor: colors.grey[500],
          color: 'white',
          fontWeight: 500,
        }}
      />
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <span className="global_loader"></span>
      </div>
    );
  }

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: { xs: 0.5, sm: 1 },
          margin: { xs: 1, sm: 2 },
          marginLeft: { xs: 1, sm: 2, md: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>KYC Management</h3>
          </Box>
          <Divider />
          <Box sx={{ padding: { xs: 1, sm: 2, md: 4 } }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <Chip
                label={`Total KYC records: ${totalCount}`}
                size="small"
                sx={{ p: 1 }}
              />
              <TextField
                label="KYC ID"
                variant="outlined"
                size="small"
                value={kycIdFilter}
                onChange={(e) => setKycIdFilter(e.target.value)}
                sx={{
                  minWidth: 100,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  },
                }}
              />
              <TextField
                label="Name"
                variant="outlined"
                size="small"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Partial match"
                sx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  },
                }}
              >
                <InputLabel>KYC type</InputLabel>
                <Select
                  value={kycTypeFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setKycTypeFilter(next);
                    setPage(0);
                    fetchPendingKYC({
                      page: 0,
                      pageSize: rowsPerPage,
                      kycType: next,
                    });
                  }}
                  label="KYC type"
                >
                  <DropdownItem value="">All</DropdownItem>
                  <DropdownItem value="manual">manual</DropdownItem>
                  <DropdownItem value="digilocker">digilocker</DropdownItem>
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  },
                }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStatusFilter(next);
                    setPage(0);
                    fetchPendingKYC({
                      page: 0,
                      pageSize: rowsPerPage,
                      status: next,
                    });
                  }}
                  label="Status"
                >
                  <DropdownItem value="">All</DropdownItem>
                  <DropdownItem value="in review">In review</DropdownItem>
                  <DropdownItem value="approved">Approved</DropdownItem>
                  <DropdownItem value="reject">Rejected</DropdownItem>
                  <DropdownItem value="kyc not done">KYC Not Done</DropdownItem>
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <DatePicker
                    label="From date"
                    value={fromDate}
                    onChange={(newDate) => setFromDate(newDate)}
                    format="DD-MM-YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 130,
                          '& .MuiOutlinedInput-root': {
                            height: '36px',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To date"
                    value={toDate}
                    onChange={(newDate) => setToDate(newDate)}
                    format="DD-MM-YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 130,
                          '& .MuiOutlinedInput-root': {
                            height: '36px',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
              <IconButton
                onClick={handleSearch}
                sx={{
                  bgcolor: colors.swiftcarry[100],
                  color: 'white',
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  '&:hover': { bgcolor: colors.swiftcarry[100] },
                }}
              >
                <Search fontSize="medium" />
              </IconButton>
              <IconButton
                onClick={handleReset}
                sx={{
                  border: `2px solid ${colors.swiftcarry[100]}`,
                  color: colors.swiftcarry[100],
                  backgroundColor: 'white',
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: `${colors.swiftcarry[100]}10`,
                  },
                }}
              >
                <Refresh fontSize="medium" />
              </IconButton>
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  minWidth: { xs: 800, sm: 650 },
                  '& .MuiTableCell-root': {
                    padding: { xs: '6px 4px', sm: '16px' },
                    fontSize: { xs: '11px', sm: '14px' },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>KYC ID</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      User ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Aadhaar Number
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created Date
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.kycId}>
                        <TableCell>{row.kycId || 'N/A'}</TableCell>
                        <TableCell>{row.userId || 'N/A'}</TableCell>
                        <TableCell>{row.userDetails?.name || 'N/A'}</TableCell>
                        <TableCell>{row.aadhaarNumber || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.type || 'N/A'}
                            size="small"
                            sx={{
                              bgcolor:
                                row.type === 'manual'
                                  ? colors.blueAccent[100]
                                  : colors.greenAccent[100],
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>{renderKycStatusChip(row.status)}</TableCell>
                        <TableCell>{formatToIST(row.createdOn)}</TableCell>
                        <TableCell>
                          {(() => {
                            const status = (row.status || '').toLowerCase();
                            const canReview = status === 'in review';
                            return canReview ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleManualKyc(row)}
                                sx={{
                                  bgcolor: colors.swiftcarry[100],
                                  fontSize: '11px',
                                  minHeight: '32px',
                                  py: 0.5,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    bgcolor: colors.swiftcarry[100],
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                  },
                                }}
                              >
                                Review KYC
                              </Button>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Review Completed
                              </Typography>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No KYC records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalCount > 0 && (
              <Box
                sx={{
                  mt: 2,
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: '#fafafa',
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      padding: '16px 24px',
                      minHeight: '64px',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    },
                    '& .MuiTablePagination-selectLabel': {
                      marginBottom: 0,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#666',
                    },
                    '& .MuiTablePagination-displayedRows': {
                      marginBottom: 0,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#666',
                    },
                    '& .MuiTablePagination-select': {
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      padding: '4px 8px',
                      marginLeft: '8px',
                      marginRight: '16px',
                      '&:focus': {
                        borderColor: colors.swiftcarry[100],
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: '20px',
                      '& .MuiIconButton-root': {
                        padding: '8px',
                        margin: '0 2px',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: colors.swiftcarry[100],
                          borderColor: colors.swiftcarry[100],
                          color: 'white',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          borderColor: '#e0e0e0',
                          color: '#ccc',
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Manual KYC Review Modal */}
      <Modal
        open={kycModalOpen}
        onClose={() => {
          setKycModalOpen(false);
          resetImageControls();
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: '1200px',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {selectedCustomer && kycDocuments && (
            <>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid #e0e0e0',
                  position: 'relative',
                }}
              >
                <IconButton
                  onClick={() => {
                    setKycModalOpen(false);
                    resetImageControls();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'text.secondary',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                      color: 'text.primary',
                    },
                    zIndex: 1,
                  }}
                  aria-label="close"
                >
                  <Close />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Manual KYC Review - {selectedCustomer.json?.name || 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>KYC ID:</strong> {selectedCustomer.kycId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>User ID:</strong> {selectedCustomer.userId || 'N/A'}
                  </Typography>
                  {selectedCustomer.clientId && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Client ID:</strong> {selectedCustomer.clientId}
                    </Typography>
                  )}
                  {selectedCustomer.type && (
                    <Chip
                      label={selectedCustomer.type}
                      size="small"
                      sx={{
                        bgcolor:
                          selectedCustomer.type === 'manual'
                            ? colors.blueAccent[100]
                            : colors.greenAccent[100],
                        color: 'white',
                        height: 24,
                      }}
                    />
                  )}
                  {selectedCustomer.status &&
                    renderKycStatusChip(selectedCustomer.status, 'small')}
                </Box>
                {(selectedCustomer.createdOn || selectedCustomer.updatedOn) && (
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}
                  >
                    {selectedCustomer.createdOn && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Created:</strong>{' '}
                        {formatToIST(selectedCustomer.createdOn)}
                      </Typography>
                    )}
                    {selectedCustomer.updatedOn && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Updated:</strong>{' '}
                        {formatToIST(selectedCustomer.updatedOn)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      📄 KYC Details
                    </Typography>

                    {/* Personal Information - Consolidated */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#f0f8ff',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                      >
                        👤 Personal Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Name:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Aadhaar Number:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.aadhaarNumber || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Date of Birth:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.dob || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Gender:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.gender?.toUpperCase() ===
                            'M'
                              ? 'Male'
                              : selectedCustomer.json?.gender?.toUpperCase() ===
                                'F'
                                ? 'Female'
                                : selectedCustomer.json?.gender || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Address Details - Consolidated (only when at least one field exists) */}
                    {selectedCustomer.json &&
                      (selectedCustomer.json.address1 ||
                        selectedCustomer.json.address2 ||
                        selectedCustomer.json.dist ||
                        selectedCustomer.json.state ||
                        selectedCustomer.json.pincode ||
                        selectedCustomer.json.country) && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: '#fffbf0',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                        >
                            📍 Address Details
                        </Typography>
                        <Grid container spacing={2}>
                          {(selectedCustomer.json.address1 ||
                              selectedCustomer.json.address2) && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Address:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {[
                                  selectedCustomer.json.address1,
                                  selectedCustomer.json.address2,
                                ]
                                  .filter(Boolean)
                                  .join(', ') || 'N/A'}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.json.dist && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>District:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.json.dist}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.json.state && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>State:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.json.state}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.json.pincode && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Pincode:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.json.pincode}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.json.country && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Country:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.json.country}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Account Details - Only if userDetails exists */}
                    {selectedCustomer.userDetails && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: '#f9f9f9',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                        >
                          📧 Account Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            {selectedCustomer.userDetails.email && (
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  <strong>Email:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                  {selectedCustomer.userDetails.email}
                                </Typography>
                              </>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {selectedCustomer.userDetails.phone && (
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  <strong>Phone:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                  {selectedCustomer.userDetails.phone}
                                </Typography>
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Aadhaar Verification */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 3,
                        border: '2px solid #e3f2fd',
                        borderRadius: 3,
                        bgcolor:
                          'linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%)',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: colors.swiftcarry[100],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                          }}
                        >
                          <Typography sx={{ color: 'white', fontSize: '16px' }}>
                            🆔
                          </Typography>
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: '#1976d2' }}
                        >
                          Aadhaar Verification
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <TextField
                          value={aadhaarNumber}
                          onChange={(e) => {
                            const formatted = formatAadhaar(e.target.value);
                            if (formatted.replace(/\s/g, '').length <= 12) {
                              setAadhaarNumber(formatted);
                              setAadhaarError('');
                            }
                          }}
                          placeholder="XXXX XXXX XXXX"
                          error={!!aadhaarError}
                          helperText={aadhaarError}
                          size="small"
                          inputProps={{
                            maxLength: 14,
                            style: {
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              letterSpacing: '1px',
                            },
                          }}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleAadhaarVerify}
                          disabled={!aadhaarNumber.trim()}
                          sx={{
                            bgcolor: colors.swiftcarry[100],
                            minWidth: 100,
                            height: 40,
                          }}
                        >
                          Verify
                        </Button>
                      </Box>
                    </Box>

                    {aadhaarVerified && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #4caf50',
                          borderRadius: 2,
                          bgcolor: '#e8f5e9',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: '#2e7d32' }}
                        >
                          ✓ Aadhaar verified
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Verified number: {aadhaarNumber || 'N/A'}
                        </Typography>
                      </Box>
                    )}

                    {/* Existing Rejection Reason */}
                    {selectedCustomer.rejectReason && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '2px solid #ff9800',
                          borderRadius: 2,
                          bgcolor: '#fff3e0',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}
                        >
                          ⚠️ Previous Rejection Reason
                        </Typography>
                        <Typography sx={{ fontSize: '13px' }}>
                          {selectedCustomer.rejectReason}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        🚫 Rejection Reason (if applicable)
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={rejectionCategory}
                        onChange={(e) => setRejectionCategory(e.target.value)}
                        label="Select rejection category"
                        size="small"
                      >
                        <DropdownItem value="">None - Approve KYC</DropdownItem>
                        <DropdownItem value="Document Issue">
                          Document Issue
                        </DropdownItem>
                        <DropdownItem value="Data Mismatch">
                          Data Mismatch
                        </DropdownItem>
                        <DropdownItem value="Quality Issue">
                          Quality Issue
                        </DropdownItem>
                        <DropdownItem value="Other">Other</DropdownItem>
                      </TextField>

                      {/* Show text box only when a rejection category is selected */}
                      {rejectionCategory && rejectionCategory !== '' && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={rejectionReason}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 500) {
                                setRejectionReason(value);
                              }
                            }}
                            placeholder="Enter detailed rejection reason (max 500 characters)"
                            variant="outlined"
                            size="small"
                            inputProps={{ maxLength: 500 }}
                            helperText={`${rejectionReason.length}/500 characters`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#ffffffff',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {kycDocuments?.documentImages &&
                      kycDocuments.documentImages.length > 1 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}
                        >
                          {kycDocuments.documentImages[currentImageIndex]
                            ?.label || 'N/A'}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <IconButton
                            onClick={prevImage}
                            sx={{ bgcolor: '#f5f5f5' }}
                          >
                            <ArrowBack />
                          </IconButton>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {currentImageIndex + 1} of{' '}
                            {kycDocuments.documentImages.length}
                          </Typography>
                          <IconButton
                            onClick={nextImage}
                            sx={{ bgcolor: '#f5f5f5' }}
                          >
                            <ArrowForward />
                          </IconButton>
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        p: 1,
                        textAlign: 'center',
                        minHeight: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {kycDocuments?.documentImages &&
                      kycDocuments.documentImages[currentImageIndex]?.url ? (
                          <img
                            src={
                              kycDocuments.documentImages[currentImageIndex].url
                            }
                            alt={
                              kycDocuments.documentImages[currentImageIndex]
                                ?.label || 'Document Image'
                            }
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Typography color="text.secondary">
                          No document images available
                          </Typography>
                        )}
                    </Box>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setKycModalOpen(false);
                      resetImageControls();
                    }}
                    sx={{ minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                  {rejectionCategory && rejectionCategory !== '' ? (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleKycRejection}
                      disabled={loading}
                      sx={{ minWidth: 120 }}
                    >
                      Reject KYC
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleKycApproval}
                      disabled={loading}
                      sx={{
                        bgcolor: colors.swiftcarry[100],
                        minWidth: 120,
                      }}
                    >
                      Approve KYC
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ManualKYC;
